import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import {
  BP_TRANSCRIPT,
  LIST_PATIENTS,
  MAR_TRANSCRIPT,
  NOUR,
  SCENE_CAPTIONS,
  SCENE_DURATIONS,
  SCENE_ORDER,
} from './e2eDemoData';
import { getPatientDisplayName } from '../../constants/mockPatients';
import {
  AlertTriangle,
  ArrowLeft,
  ClipboardList,
  DemoBottomNav,
  DemoListRow,
  DemoMedicationCard,
  DemoNoteTypeHeader,
  DemoPatientListCard,
  DemoRecordingOverlay,
  DemoReviewScreen,
  DemoTaskCard,
  DemoTaskTabs,
  DemoVitalCard,
  Download,
  Moon,
  Pill,
  Send,
  Stethoscope,
} from './e2eDemoComponents';
import { DEMO_ROOT_CLASS } from './onboardingLayout';

function useTypeProgress(active, text, charMs = 36) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!active) {
      setProgress(0);
      return undefined;
    }
    let index = 0;
    let timer;
    const tick = () => {
      index += 1;
      setProgress(index);
      if (index < text.length) timer = setTimeout(tick, charMs);
    };
    tick();
    return () => clearTimeout(timer);
  }, [active, text, charMs]);

  return progress;
}

export default function E2EDemoAnimation() {
  const [sceneIndex, setSceneIndex] = useState(0);
  const scene = SCENE_ORDER[sceneIndex];
  const [subPhase, setSubPhase] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setSceneIndex((i) => (i + 1) % SCENE_ORDER.length);
      setSubPhase(0);
    }, SCENE_DURATIONS[scene]);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [scene]);

  useEffect(() => {
    if (scene === 'bp_record' || scene === 'mar_record') {
      const t = setTimeout(() => setSubPhase(1), 400);
      return () => clearTimeout(t);
    }
    if (scene === 'bp_review' || scene === 'mar_review') {
      const t = setTimeout(() => setSubPhase(1), 1400);
      return () => clearTimeout(t);
    }
    if (scene === 'tabs') {
      const timers = [
        setTimeout(() => setSubPhase(1), 1600),
        setTimeout(() => setSubPhase(2), 3200),
      ];
      return () => timers.forEach(clearTimeout);
    }
    setSubPhase(0);
    return undefined;
  }, [scene]);

  const bpProgress = useTypeProgress(scene === 'bp_record' && subPhase >= 1, BP_TRANSCRIPT);
  const marProgress = useTypeProgress(scene === 'mar_record' && subPhase >= 1, MAR_TRANSCRIPT);

  const nourName = getPatientDisplayName(NOUR, 'en');
  const favoritesView = scene === 'favorite' || sceneIndex > SCENE_ORDER.indexOf('favorite');
  const manualDone = scene !== 'tasks_pending';

  return (
    <div className={DEMO_ROOT_CLASS}>
      <div className="flex h-full w-full flex-col">
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-page shadow-sm">
          <div key={scene} className="absolute inset-0 overflow-hidden bg-page onboarding-fade-in">
        {(scene === 'patients' || scene === 'favorite' || scene === 'open_patient') && (
          <div className="space-y-2 p-2">
            <div className="flex gap-1.5">
              <div
                className={`min-h-[3.25rem] flex-1 rounded-2xl px-2 py-2 ${
                  !favoritesView ? 'bg-primary text-white shadow-card' : 'border border-gray-200 bg-gray-100'
                }`}
              >
                <p className="text-[10px] font-bold">All</p>
                <p className="text-[9px] opacity-90">8</p>
              </div>
              <div
                className={`min-h-[3.25rem] flex-1 rounded-2xl px-2 py-2 ${
                  favoritesView ? 'bg-primary text-white shadow-card' : 'border border-gray-200 bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-0.5">
                  <Star className={`h-2.5 w-2.5 ${favoritesView ? 'fill-white text-white' : ''}`} />
                  <p className="text-[10px] font-bold">Favorites</p>
                </div>
                <p className="text-[9px] opacity-90">{favoritesView ? '1' : '0'}</p>
              </div>
            </div>
            {LIST_PATIENTS.map((p) => (
              <DemoPatientListCard
                key={p.id}
                patient={p}
                favorite={p.id === 'p6' && favoritesView}
                selected={p.id === 'p6' && scene === 'open_patient'}
                showTap={
                  (scene === 'favorite' && p.id === 'p6') ||
                  (scene === 'open_patient' && p.id === 'p6')
                }
              />
            ))}
          </div>
        )}

        {scene === 'profile' && (
          <div className="space-y-1 p-2">
            <ArrowLeft className="h-3.5 w-3.5 text-gray-800" />
            <div className="card-surface flex items-center gap-2 p-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-mid text-xs font-bold text-primary">
                N
              </div>
              <div>
                <h1 className="text-[11px] font-bold text-gray-900">{nourName}</h1>
                <p className="text-[8px] text-gray-500">
                  Room {NOUR.room} · Pediatrics · Age {NOUR.age}
                </p>
                <p className="text-[8px] text-gray-500">{NOUR.sex}</p>
              </div>
            </div>
            <section className="card-surface p-2">
              <h3 className="text-section-label mb-0.5 flex items-center gap-1 text-[8px]">
                <AlertTriangle className="h-2.5 w-2.5" />
                Allergies
              </h3>
              <span className="inline-block rounded-full bg-critical-light px-1.5 py-0.5 text-[7px] font-semibold text-critical">
                {NOUR.allergies[0]}
              </span>
            </section>
            <section className="card-surface p-2">
              <h3 className="text-section-label mb-0.5 flex items-center gap-1 text-[8px]">
                <Stethoscope className="h-2.5 w-2.5" />
                Diagnoses
              </h3>
              <p className="text-[8px] text-gray-800">{NOUR.diagnoses[0]}</p>
            </section>
            <section className="card-surface p-2">
              <h3 className="text-section-label mb-0.5 flex items-center gap-1 text-[8px]">
                <Pill className="h-2.5 w-2.5" />
                Active Medications
              </h3>
              <p className="text-[8px] font-semibold text-gray-900">{NOUR.medications[0].name}</p>
              <p className="text-[8px] text-gray-600">
                {NOUR.medications[0].dose} · {NOUR.medications[0].frequency}
              </p>
            </section>
          </div>
        )}

        {(scene === 'tasks_pending' ||
          scene === 'manual_done' ||
          scene === 'bp_tap' ||
          scene === 'mar_tap') && (
          <div className="space-y-1.5 p-2">
            <DemoTaskTabs active="pending" />
            <DemoTaskCard
              iconKey="growth"
              title="Growth and Development Check"
              completed={manualDone}
              highlight={scene === 'manual_done'}
              showTap={scene === 'manual_done'}
            />
            <DemoTaskCard
              iconKey="vitals"
              title="Vitals"
              nestedItems={[
                {
                  label: 'Blood Pressure',
                  due: 'Due in 45m',
                  completed: false,
                  highlight: scene === 'bp_tap',
                },
              ]}
              showTap={scene === 'bp_tap'}
              highlight={scene === 'bp_tap'}
            />
            <DemoTaskCard
              iconKey="medication_admin"
              title="Medications"
              nestedItems={[
                {
                  label: 'Budesonide inhaler 100mcg',
                  due: 'Due at 08:00',
                  completed: false,
                  highlight: scene === 'mar_tap',
                },
              ]}
              showTap={scene === 'mar_tap'}
              highlight={scene === 'mar_tap'}
            />
          </div>
        )}

        {scene === 'tasks_done' && (
          <div className="space-y-1.5 p-2">
            <DemoTaskTabs active="completed" />
            <DemoTaskCard
              iconKey="vitals"
              title="Vitals"
              completed
              nestedItems={[
                {
                  label: 'Blood Pressure',
                  completed: true,
                },
              ]}
            />
            <DemoTaskCard
              iconKey="growth"
              title="Growth and Development Check"
              completed
            />
          </div>
        )}

        {scene === 'tabs' && (
          <div className="flex h-full flex-col">
            <div className="flex-1 space-y-1.5 px-2">
              {subPhase === 0 && (
                <DemoListRow initial="N" title="Nour Al-Deen" subtitle="BP 95/60 · Pulse 88" />
              )}
              {subPhase === 1 && (
                <DemoListRow
                  initial="N"
                  title="Nour Al-Deen"
                  subtitle="10:30 · Sarah Al-Hassan"
                  badge="Progress Note"
                />
              )}
              {subPhase >= 2 && (
                <DemoListRow
                  initial="N"
                  title="Nour Al-Deen"
                  subtitle="Budesonide inhaler 100mcg"
                />
              )}
            </div>
            <DemoBottomNav activeIndex={subPhase === 0 ? 2 : subPhase === 1 ? 1 : 3} />
          </div>
        )}

        {scene === 'handover' && (
          <div className="space-y-2 p-2">
            <div className="border-b border-gray-200 pb-2">
              <div className="flex items-center gap-1">
                <ClipboardList className="h-4 w-4 text-primary" />
                <div>
                  <h1 className="text-[11px] font-bold">Shift Handover</h1>
                  <p className="text-[8px] text-gray-500">Night shift</p>
                </div>
              </div>
              <span className="mt-1 inline-flex items-center gap-0.5 rounded-pill bg-indigo-100 px-1.5 py-0.5 text-[7px] font-semibold text-indigo-800">
                <Moon className="h-2.5 w-2.5" />
                Night
              </span>
            </div>
            <div className="card-surface p-2">
              <p className="text-[10px] font-semibold">
                {nourName} · Room {NOUR.room}
              </p>
              <p className="mt-1 text-[8px] text-gray-600">
                Stable pediatric asthma patient. Vitals WNL. Budesonide given as scheduled.
              </p>
              <p className="mt-1 text-[8px] text-gray-500">
                Outstanding: Pain assessment at 14:00
              </p>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-0.5 rounded-lg border border-gray-200 py-1.5 text-[8px] font-semibold text-gray-600"
              >
                <Download className="h-3 w-3" />
                PDF
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-0.5 rounded-lg bg-handover py-1.5 text-[8px] font-semibold text-white"
              >
                <Send className="h-3 w-3" />
                Submit
              </button>
            </div>
          </div>
        )}

        {scene === 'bp_record' && (
          <DemoRecordingOverlay
            transcript={BP_TRANSCRIPT}
            progress={bpProgress}
            recording={subPhase >= 1}
          />
        )}
        {scene === 'mar_record' && (
          <DemoRecordingOverlay
            transcript={MAR_TRANSCRIPT}
            progress={marProgress}
            recording={subPhase >= 1}
          />
        )}
        {scene === 'bp_review' && (
          <DemoReviewScreen noteType="vitals" saving={subPhase >= 1}>
            <DemoNoteTypeHeader noteType="vitals" />
            <div className="flex flex-col gap-1.5">
              <DemoVitalCard vitalKey="bp" label="Blood Pressure" value="95/60" unit="mmHg" />
              <DemoVitalCard vitalKey="pulse" label="Pulse" value="88" unit="bpm" />
            </div>
          </DemoReviewScreen>
        )}
        {scene === 'mar_review' && (
          <DemoReviewScreen noteType="medication_admin" saving={subPhase >= 1}>
            <DemoNoteTypeHeader noteType="medication_admin" />
            <DemoMedicationCard
              drugName="Budesonide inhaler"
              dose="100mcg"
              route="Inhaled"
              response="Tolerated well"
            />
          </DemoReviewScreen>
        )}
        </div>
        </div>
        <p className="mt-1 shrink-0 text-center text-[9px] font-medium text-primary sm:text-[10px]">
          {SCENE_CAPTIONS[scene]}
        </p>
      </div>
    </div>
  );
}
